import React, { useEffect, useMemo } from 'react'
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome'
import { Order as OrderType } from 'depoto-core'
import { formatPrice } from './OrderPrice'
import { Link } from 'react-router-dom'
import { useCore } from '../hooks'
import {
  faBox,
  faBoxCircleCheck,
  faCashRegister,
  faHeadset,
  faMessage,
  faMoneyBill,
  faMoneyBill1Wave,
  faReceipt,
  faSackDollar,
  faWarehouseFull,
} from '@fortawesome/pro-duotone-svg-icons'
import {
  faArrowProgress,
  faCalendar,
  faCartFlatbedEmpty,
  faClock,
  faExpandAlt,
  faFragile,
  faInboxIn,
  faReceipt as faReceiptSolid,
  faScannerGun,
  faTimelineArrow,
  faTruckFast,
  faUserVneck,
} from '@fortawesome/pro-solid-svg-icons'
import { faClockRotateLeft } from '@fortawesome/pro-regular-svg-icons'
import tinycolor from 'tinycolor2'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { formatDate } from '../lib'
import 'dayjs/locale/cs'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { Tag } from './Tag'
import { FragmentType, graphql, useFragment } from '../gql'
import { OrderProgressStatus, OrderStatusIndicator } from '../views/OrderGroupDetails/components/OrderStatusIndicator'
import { Tooltip, TooltipContent, TooltipTrigger } from './shadcn/tooltip'
import { ProcessStatusIcon } from './ProcessStatusIcon'

dayjs.locale('cs')
dayjs.extend(relativeTime)

// Cache for carrier color calculations to avoid expensive tinycolor operations
const carrierColorCache = new Map<string, { backgroundColor: string; iconColor: string }>()

function getCarrierColors(carrierColor: string | null | undefined): { backgroundColor: string; iconColor: string } {
  const color = carrierColor || 'rgb(150, 150, 150)'

  // Check if we already have this color cached
  if (carrierColorCache.has(color)) {
    return carrierColorCache.get(color)!
  }

  // Calculate background color
  const backgroundColor = tinycolor.mix(tinycolor(color), '#fff', 80).toString()

  // Calculate icon color with proper contrast
  let iconColor = backgroundColor
  for (let i = 0; i < 100; i++) {
    if (tinycolor.readability(iconColor, backgroundColor) > 1.6) {
      break
    }
    iconColor = tinycolor(iconColor).darken(1).toString()
  }

  // Cache the result
  const result = { backgroundColor, iconColor }
  carrierColorCache.set(color, result)

  return result
}

const OrderListItem_OrderFragment = graphql(`
  fragment OrderListItem_Order on Order {
    id
    reservationNumber
    dateCreated
    isPaid
    quantityUnavailable
    boxes
    dateExpedition
    carrier {
      id
      name
      color
    }
    note
    privateNote
    depots {
      id
      name
    }
    shippingAddress {
      id
      firstName
      lastName
      street
      city
      country
    }
    checkout {
      id
      name
    }
    priority
    externalReference
    processStatusUpdated
    tags {
      id
      ...TagComponent_TagFragment
    }
    items {
      id
      product {
        id
        isFragile
        isOversize
      }
    }
    processStatus {
      id
      name
    }
  }
`)

export const Order: React.FC<{
  order: FragmentType<typeof OrderListItem_OrderFragment>
  progress?: OrderProgressStatus
  groupId?: number
  isUpdating?: boolean
  historyItemTimestamp?: number
}> = React.memo(props => {
  const order = useFragment(OrderListItem_OrderFragment, props.order)

  const { backgroundColor: color, iconColor } = getCarrierColors(order.carrier?.color)

  const { company } = useCore()
  // @ts-ignore
  const hasChildren = company?.children?.length > 0

  const {
    reservationNumber,
    note,
    privateNote,
    isPaid,
    quantityUnavailable,
    depots = [],
    boxes = [],
    processStatusUpdated = 0,
    dateExpedition = 0,
  } = order

  const statusIcons: {
    iconProps: FontAwesomeIconProps
    active: boolean
    warning: boolean
    tooltip: string
    showOnlyWarning: boolean
  }[] = useMemo(
    () => [
      {
        iconProps: {
          icon: faMoneyBill1Wave,
        },
        active: true,
        warning: !isPaid,
        tooltip: isPaid ? 'Zaplaceno' : 'Nezaplaceno',
        showOnlyWarning: true,
      },
      {
        iconProps: {
          icon: quantityUnavailable === 0 ? faBoxCircleCheck : faBox,
        },
        active: true,
        warning: quantityUnavailable !== 0,
        tooltip: quantityUnavailable === 0 ? 'Nechybí zboží' : 'Chybí zboží',
        showOnlyWarning: true,
      },
      {
        iconProps: {
          icon: faWarehouseFull,
          size: 'lg',
        },
        active: depots.length === 1,
        warning: depots.length > 1,
        tooltip: depots.length === 1 ? 'Zboží z 1 skladu' : 'Zboží z více skladů',
        showOnlyWarning: true,
      },
      {
        iconProps: {
          icon: faMessage,
          size: 'lg',
        },
        active: !!note?.length || !!privateNote?.length,
        warning: !!privateNote?.length,
        tooltip: privateNote?.length
          ? privateNote?.length < 30
            ? privateNote
            : 'Interní poznámka'
          : note?.length
            ? note?.length <= 30
              ? note
              : 'Poznámka'
            : 'Nemá poznámku',
        showOnlyWarning: false,
      },
      {
        iconProps: {
          icon: faFragile,
          size: 'lg',
        },
        active: order?.items?.some(item => item.product?.isFragile),
        warning: order?.items?.some(item => item.product?.isFragile),
        tooltip: order?.items?.some(item => item.product?.isFragile) ? 'Křehké zboží' : 'Nemá křehké zboží',
        showOnlyWarning: true,
      },
      {
        iconProps: {
          icon: faExpandAlt,
          size: 'lg',
        },
        active: order?.items?.some(item => item.product?.isOversize),
        warning: order?.items?.some(item => item.product?.isOversize),
        tooltip: order?.items?.some(item => item.product?.isOversize) ? 'Nadměrné zboží' : 'Nemá nadměrné zboží',
        showOnlyWarning: true,
      },
    ],
    [isPaid, quantityUnavailable, depots, note, privateNote, order?.items],
  )

  const renderStatusIcons = () => {
    const country = order.shippingAddress?.country || 'CZ'
    const countryIcon = {
      // CZ: '🇨🇿', // show country code for now
      // SK: '🇸🇰',
    }[country]
    return (
      <div className={'flex flex-row gap-3 items-center'}>
        {statusIcons
          .filter(v => v.active && (v.showOnlyWarning ? v.warning : true))
          .map((statusIcon, index) => {
            const { iconProps, active, warning, tooltip } = statusIcon
            return (
              <Tooltip key={index}>
                <TooltipTrigger asChild>
                  <div>
                    <FontAwesomeIcon
                      size={'lg'}
                      style={{
                        // @ts-ignore
                        '--fa-primary-color': warning
                          ? 'rgb(245, 0, 0)'
                          : active
                            ? 'rgb(150,206,126)'
                            : 'rgb(227,227,227)',
                        '--fa-secondary-color': warning
                          ? 'rgb(255, 0, 0)'
                          : active
                            ? 'rgb(39,178,13)'
                            : 'rgb(152,152,152)',
                        '--fa-secondary-opacity': 0.5,
                        opacity: active ? 1 : 0.5,
                        ...(iconProps.style || {}),
                      }}
                      {...iconProps}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>{tooltip}</TooltipContent>
              </Tooltip>
            )
          })}
        {props.progress && <OrderStatusIndicator status={props.progress} />}
        {!props.progress && (
          <>
            {' '}
            {countryIcon ? (
              <img src={`https://emojicdn.elk.sh/${countryIcon}`} alt={country} className={'h-6'} />
            ) : (
              <span>{country}</span>
            )}
          </>
        )}
      </div>
    )
  }

  const orderDetailItems: {
    label: string
    icon: IconProp
    value: string | undefined
    isVisible: boolean

    // Explicitly show if empty – means that if explShowIfEmpty is true, even if isVisible is false, item will be shown as grayed "Nezadano"
    explShowIfEmpty: boolean
  }[] = useMemo(
    () =>
      [
        {
          label: 'Procesní stav',
          icon: faTimelineArrow,
          value: order.processStatus?.name,
          isVisible: !!order.processStatus?.name && !!props.groupId,
          explShowIfEmpty: false,
        },
        {
          label: 'Doprava',
          icon: faTruckFast,
          value: order.carrier?.name,
          isVisible: !!order.carrier?.name,
          explShowIfEmpty: true,
        },
        {
          label: 'Zákazník',
          icon: faUserVneck,
          value: `${order.shippingAddress?.firstName} ${order.shippingAddress?.lastName}`,
          isVisible: !!order.shippingAddress?.firstName || !!order.shippingAddress?.lastName,
          explShowIfEmpty: true,
        },
        {
          label: 'External reference',
          icon: faHeadset,
          value: order.externalReference,
          isVisible: !!order?.externalReference,
          explShowIfEmpty: false,
        },
        {
          label: 'Stav změněn',
          icon: faArrowProgress,
          value: `${formatDate(processStatusUpdated, true)}`,
          isVisible: !!processStatusUpdated,
          explShowIfEmpty: false,
        },
        {
          label: 'Datum expedice',
          icon: faScannerGun,
          value: `${formatDate(dateExpedition, false)}`,
          isVisible: !!dateExpedition && !processStatusUpdated,
          explShowIfEmpty: false,
        },
        {
          label: 'Datum vytvoření',
          icon: faInboxIn,
          value: `${formatDate(order?.dateCreated, true)}`,
          // value: `${formatDate(order?.dateCreated, false)} (${dayjs(order?.dateCreated).fromNow()})`,
          isVisible: !!order?.dateCreated,
          explShowIfEmpty: false,
        },
        {
          label: 'Pokladna',
          icon: faCashRegister,
          value: order.checkout?.name,
          isVisible: !!order?.checkout?.name,
          explShowIfEmpty: false,
        },
        {
          label: 'Přepravka',
          icon: faCartFlatbedEmpty,
          value: boxes.join(', '),
          isVisible: boxes.length > 0,
          explShowIfEmpty: false,
        },
      ].filter(v => v.isVisible || v.explShowIfEmpty),
    [props.groupId, order],
  )

  const renderOrderDetailItems = () => {
    const hasFragiles = order?.items?.filter(oi => oi.product?.isFragile).length! > 0
    const hasOversized = order?.items?.filter(oi => oi.product?.isOversize).length! > 0

    return (
      <div className="p-3">
        {/* {orderDetailItems.map((el, i) => (
          <div className={'flex flex-row items-center gap-3'} key={el.label} data-tippy-content={el.label}>
            <div className={'flex w-7 h-7 items-center justify-center'}>
              <FontAwesomeIcon icon={el.icon} color={iconColor} size={'lg'} />
            </div>
            <span className={'font-semibold'} style={!el.isVisible ? { color: 'rgba(0, 0, 0, 0.3)' } : {}}>
              {el.isVisible ? el.value : el.explShowIfEmpty ? 'Nezadano' : ''}
            </span>
          </div>
        ))} */}
        {orderDetailItems.map((el, i) => (
          <Tooltip key={el.label}>
            <TooltipTrigger asChild>
              <div className={'flex flex-row items-center gap-3'}>
                <div className={'flex w-7 h-7 items-center justify-center'}>
                  <FontAwesomeIcon icon={el.icon} color={iconColor} size={'lg'} />
                </div>
                <span className={'font-semibold'} style={!el.isVisible ? { color: 'rgba(0, 0, 0, 0.3)' } : {}}>
                  {el.isVisible ? el.value : el.explShowIfEmpty ? 'Nezadano' : ''}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent>{el.label}</TooltipContent>
          </Tooltip>
        ))}
        <div className="flex flex-row flex-wrap items-center gap-2 mt-2">
          <div className={'flex items-center gap-2 flex-wrap'}>
            {(order?.tags || []).map(tag => (
              <div key={tag.id}>
                <Tag tag={tag} size="compact" />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const LinkElement = props.isUpdating ? ({ children, ...props }) => <div {...props}>{children}</div> : Link

  return (
    <span className="flex flex-col rounded-lg bg-gray-100 border border-gray-200">
      <LinkElement
        to={props.groupId ? `/order/${order.id}?group=${props.groupId}` : `/order/${order.id}`}
        className={`flex rounded-lg flex-1 flex-col ${props.isUpdating ? 'opacity-50' : ''}`}
        style={{ backgroundColor: color, breakInside: 'avoid' }}>
        <div className={'flex flex-row gap-3 items-center justify-between px-3 py-2 rounded-t-lg bg-gray-50'}>
          <div className={'flex flex-row gap-3 items-center '}>
            <ProcessStatusIcon status={order.processStatus?.id || 'received'} />
            <span className={'font-bold text-lg'}>{reservationNumber}</span>
          </div>
          <div className={'flex flex-row gap-3 items-center'}>{renderStatusIcons()}</div>
        </div>
        {renderOrderDetailItems()}
      </LinkElement>
      {props.historyItemTimestamp && (
        <div className="flex flex-row items-center gap-2 bg-gray-100 p-2 px-3 rounded-b-lg">
          <FontAwesomeIcon icon={faClockRotateLeft} className="text-gray-500" />
          <span className="text-sm text-gray-500">
            <b>{formatDate(new Date(props.historyItemTimestamp).toISOString(), true)}</b> (
            {dayjs(props.historyItemTimestamp).fromNow()})
          </span>
        </div>
      )}
    </span>
  )
})
